/*
  Swatch slider + variant switching for collection-grid cards rendered by
  snippets/card-product-cvg.liquid.

  Mirrors the inline script in sections/section-collection-variant-grid.liquid,
  with three differences:
    - binds to any [data-cvg-card] rather than one section id,
    - is idempotent, so it can safely re-run after the facets AJAX swaps the
      grid (it listens for the theme's `grid:updated` event),
    - reads swatch data attributes produced by the collection page's own swatch
      logic (data-variant-title / data-variant-price / data-variant-url).

  These cards intentionally have no add-to-cart button, which makes
  initCardScripts() in brd-java.js bail out early, so this file owns the swatch
  behaviour for them outright - there is no double binding.
*/
(function () {
  'use strict';

  // Every section that shows these cards loads this file, so it can arrive
  // more than once on a page. The first copy covers every card; the rest stop.
  if (window.cvgCardLoaded) return;
  window.cvgCardLoaded = true;

  /*
    The facet overlay is a near-white wash, which is invisible on a white or
    near-white swatch. Those get a grey wash instead (see
    .cvg-card__swatch--light).

    Detected by relative luminance off the computed background rather than by
    matching colour strings: swatch values reach the DOM as hex, rgb() or a
    url() depending on whether they came from a native swatch, the theme's
    swatch_config, or an uploaded image. Anything non-parseable (image
    swatches) simply keeps the default overlay.
  */
  // 0.95 sits in a wide gap in this catalogue's palette: pure white is 1.0,
  // the next palest colour (lavender) is 0.908 and everything else is <= 0.852.
  // So genuine whites qualify and pale tints, which the white wash still reads
  // against, do not.
  var LIGHT_SWATCH_LUMINANCE = 0.95;

  function markLightSwatches(swatches) {
    swatches.forEach(function (swatch) {
      var parts = getComputedStyle(swatch).backgroundColor.match(/rgba?\(([^)]+)\)/);
      if (!parts) return;
      var rgb = parts[1].split(',').map(parseFloat);
      if (rgb.length < 3 || rgb.some(isNaN)) return;
      var luminance = (0.2126 * rgb[0] + 0.7152 * rgb[1] + 0.0722 * rgb[2]) / 255;
      swatch.classList.toggle('cvg-card__swatch--light', luminance >= LIGHT_SWATCH_LUMINANCE);
    });
  }

  function initCard(card) {
    if (card.dataset.cvgCardInitialized === 'true') return;
    card.dataset.cvgCardInitialized = 'true';

    var image = card.querySelector('.cvg-card__image');
    var colorName = card.querySelector('.cvg-card__color-name');
    var priceEl = card.querySelector('.cvg-card__price');
    var titleLink = card.querySelector('.cvg-card__title-link');
    var titleText = card.querySelector('.cvg-card__title');
    var mediaLink = card.querySelector('.cvg-card__media-link');
    var row = card.querySelector('.cvg-card__swatch-row');
    var viewport = card.querySelector('.cvg-card__swatches');
    var track = card.querySelector('.cvg-card__swatches-track');
    var swatches = Array.prototype.slice.call(card.querySelectorAll('.cvg-card__swatch'));
    var prevArrow = card.querySelector('.cvg-card__arrow--prev');
    var nextArrow = card.querySelector('.cvg-card__arrow--next');

    markLightSwatches(swatches);

    if (!viewport || !track || !swatches.length) return;

    var slideIndex = 0;

    function getVar(name, fallback) {
      var value = parseFloat(getComputedStyle(viewport).getPropertyValue(name));
      return isNaN(value) ? fallback : value;
    }

    function getGap() {
      return getVar('--swatch-gap', 8);
    }

    // With --swatch-target set (phones, tablets, touch screens), as many as
    // fit at about that size,
    // up to --swatch-visible, so a narrow card shows fewer, larger swatches
    // rather than the same count shrunk to a few pixels. Otherwise the
    // count is --swatch-visible itself.
    function getVisibleCount() {
      var most = getVar('--swatch-visible', 7);
      var target = getVar('--swatch-target', 0);
      if (!target) return most;
      var gap = getGap();
      var fit = Math.round((getInnerWidth() + gap) / (target + gap));
      return Math.max(1, Math.min(most, fit));
    }

    // clientWidth includes the viewport's padding (which exists to stop the
    // active swatch's ring being clipped), so measure the content box instead
    // or the swatches are sized too wide and the last one overflows the clip.
    function getInnerWidth() {
      var styles = getComputedStyle(viewport);
      return (
        viewport.clientWidth -
        (parseFloat(styles.paddingLeft) || 0) -
        (parseFloat(styles.paddingRight) || 0)
      );
    }

    function sizeSwatches() {
      var visible = getVisibleCount();
      var gap = getGap();
      var available = getInnerWidth() - gap * (visible - 1);
      var size = Math.max(0, available / visible);
      viewport.style.setProperty('--swatch-size', size + 'px');
    }

    function getStep() {
      return getVar('--swatch-size', 18) + getGap();
    }

    function getMaxIndex() {
      return Math.max(0, swatches.length - getVisibleCount());
    }

    function clampIndex(index) {
      return Math.max(0, Math.min(index, getMaxIndex()));
    }

    // The slider only engages once there are more swatches than fit. Below
    // that the row is static: no arrows, no transform, swatches packed from
    // the left. The visible count changes with the breakpoint and, on phones,
    // the card's width, so this is re-evaluated on resize rather than decided
    // once.
    function isSliderActive() {
      return swatches.length > getVisibleCount();
    }

    function render(animate) {
      var active = isSliderActive();
      if (row) row.classList.toggle('is-slider-active', active);

      if (!active) {
        slideIndex = 0;
        track.style.transition = 'none';
        track.style.transform = 'translateX(0px)';
        void track.offsetHeight;
        track.style.transition = '';
        if (prevArrow) prevArrow.disabled = true;
        if (nextArrow) nextArrow.disabled = true;
        return;
      }

      track.style.transition = animate === false ? 'none' : '';
      track.style.transform = 'translateX(-' + slideIndex * getStep() + 'px)';
      if (!animate) {
        // force reflow so a later animated call isn't merged with this one
        void track.offsetHeight;
        track.style.transition = '';
      }
      if (prevArrow) prevArrow.disabled = slideIndex <= 0;
      if (nextArrow) nextArrow.disabled = slideIndex >= getMaxIndex();
    }

    /*
      The arrows move a whole row of new colours at a time rather than one:
      exactly as many as are showing, from wherever the row is now, clamped at
      either end.

      Stepping from the current position rather than snapping to a fixed page
      grid is deliberate. A card opens scrolled to its active colour, which is
      often near the end, and a grid would make the first press back land on
      the grid line just before it - four new colours out of seven, with a
      dozen unseen still to the left. This way a press falls short only when
      there genuinely are fewer colours left in that direction.

      Visible count comes from getVisibleCount: 7 on desktop, and on phones,
      tablets and touch screens as many as fit at a thumb-friendly size.
    */
    function pageBy(direction) {
      slideIndex = clampIndex(slideIndex + direction * getVisibleCount());
      render(true);
    }

    function bringIntoView(index, animate) {
      if (!isSliderActive()) {
        render(animate);
        return;
      }
      var visible = getVisibleCount();
      if (index < slideIndex) {
        slideIndex = index;
      } else if (index >= slideIndex + visible) {
        slideIndex = index - visible + 1;
      }
      slideIndex = clampIndex(slideIndex);
      render(animate);
    }

    swatches.forEach(function (swatch, index) {
      swatch.addEventListener('click', function (event) {
        event.preventDefault();
        if (swatch.classList.contains('is-active')) return;

        swatches.forEach(function (s) {
          s.classList.remove('is-active');
          s.setAttribute('aria-pressed', 'false');
        });
        swatch.classList.add('is-active');
        swatch.setAttribute('aria-pressed', 'true');

        var newImage = swatch.getAttribute('data-image');
        var newSrcset = swatch.getAttribute('data-srcset');
        var newAlt = swatch.getAttribute('data-alt');
        var newName = swatch.getAttribute('data-variant-title');
        var newPrice = swatch.getAttribute('data-variant-price');
        var newUrl = swatch.getAttribute('data-variant-url');
        // shade swatches are separate products, so the title changes with them
        var newTitle = swatch.getAttribute('data-card-title');

        if (image && newImage) {
          // srcset must be updated (or cleared) alongside src - browsers
          // prefer a matching srcset candidate over a plain src change
          if (newSrcset) {
            image.srcset = newSrcset;
          } else {
            image.removeAttribute('srcset');
          }
          image.src = newImage;
          if (newAlt) image.alt = newAlt;
        }
        if (colorName && newName) colorName.textContent = newName;
        if (priceEl && newPrice) priceEl.textContent = newPrice;
        if (titleText && newTitle) titleText.textContent = newTitle;

        if (newUrl) {
          [titleLink, mediaLink].forEach(function (link) {
            if (link) link.href = newUrl;
          });
        }

        bringIntoView(index, true);
      });
    });

    if (prevArrow) {
      prevArrow.addEventListener('click', function () {
        pageBy(-1);
      });
    }
    if (nextArrow) {
      nextArrow.addEventListener('click', function () {
        pageBy(1);
      });
    }

    var initialActiveIndex = parseInt(viewport.getAttribute('data-active-index'), 10) || 0;
    sizeSwatches();
    bringIntoView(initialActiveIndex, false);
    var lastVisible = getVisibleCount();

    function activeIndex() {
      for (var i = 0; i < swatches.length; i++) {
        if (swatches[i].classList.contains('is-active')) return i;
      }
      return 0;
    }

    card._cvgResize = function () {
      sizeSwatches();
      // On phones and tablets the count follows the card's width. When it
      // changes, the old position means something else, so the row starts
      // again from the chosen colour.
      var visible = getVisibleCount();
      if (visible !== lastVisible) {
        lastVisible = visible;
        bringIntoView(activeIndex(), false);
        return;
      }
      slideIndex = clampIndex(slideIndex);
      render(false);
    };

    /*
      A card can change width without the window doing so: recommendation
      and recently viewed rows lay their cards out in a slider after this has
      sized them, and fonts and scrollbars arrive late. So each card watches
      its own row. The row's width comes from the card, not the swatches, so
      resizing the swatches does not set this off again.
    */
    if ('ResizeObserver' in window) {
      var lastWidth = viewport.clientWidth;
      new ResizeObserver(function () {
        var width = viewport.clientWidth;
        if (width === lastWidth) return;
        lastWidth = width;
        card._cvgResize();
      }).observe(viewport);
    }
  }

  function initAll() {
    document.querySelectorAll('[data-cvg-card]').forEach(initCard);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // The facets AJAX replaces #ProductGridContainer wholesale, so the new cards
  // need initialising. The data flag keeps surviving cards from binding twice.
  document.addEventListener('grid:updated', initAll);
  document.addEventListener('shopify:section:load', initAll);

  /*
    Product recommendations and recently viewed products arrive later and
    quietly. Their elements fetch the section once it scrolls near the
    viewport and assign the result straight to innerHTML - no event, and
    shopify:section:load does not fire for it - so the cards they bring in had
    never been initialised and their swatches did nothing. Watching the
    elements covers that without touching the theme's classes.
  */
  document.querySelectorAll('product-recommendations, recently-viewed-products').forEach(function (host) {
    new MutationObserver(function () {
      if (host.querySelector('[data-cvg-card]')) initAll();
    }).observe(host, { childList: true, subtree: true });
  });

  // Without ResizeObserver (each card's own, above), the window's resize
  // is the only signal there is.
  if (!('ResizeObserver' in window)) {
    var resizeRaf = null;
    window.addEventListener('resize', function () {
      if (resizeRaf) cancelAnimationFrame(resizeRaf);
      resizeRaf = requestAnimationFrame(function () {
        document.querySelectorAll('[data-cvg-card]').forEach(function (card) {
          if (typeof card._cvgResize === 'function') card._cvgResize();
        });
      });
    });
  }
})();
